'use client';

import type { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { useMySizesGarage } from '@util/hooks/useMySizesGarage';
import type { FacetCounts } from 'models/product';
import type { Bike } from 'models/shared';
import type { UserDocument, UserSizes } from 'models/user';
import dynamic from 'next/dynamic';
import Link from 'next/link';

type MySizesGarageProps = {
  userDoc: UserDocument | null | undefined;
  facet_counts: FacetCounts | undefined;
  handleBikeSelection: (args: {
    bike?: Bike | null;
    remove?: boolean;
    bikes?: Bike[];
  }) => void;
  filterState: CategoryPageSideFilters;
  handleSizeSelection: (sizes: UserSizes) => void;
  bikePendingFilters?: {
    bikeTypes: string[] | undefined;
    bikeModels: string[] | undefined;
    bikeYears: string[] | undefined;
    bikeMakes: string[] | undefined;
  };
  sizePendingFilters?: {
    letterSizes: string[] | undefined;
    numberSizes: string[] | undefined;
  };
};
const GarageFilter = dynamic(() => import('./components/GarageFilter'), {
  ssr: false,
});

const GearFilter = dynamic(() => import('./components/GearFilter'), {
  ssr: false,
});
export default function MySizesGarage({
  userDoc,
  facet_counts,
  handleBikeSelection,
  filterState,
  handleSizeSelection,
  bikePendingFilters,
  sizePendingFilters,
}: MySizesGarageProps) {
  const {
    selectedBikes,
    setSelectedBikes,
    selectedSizes,
    isEditGarageModalOpen,
    setIsEditGarageModalOpen,
    isMobileEditGarageModalOpen,
    setIsMobileEditGarageModalOpen,
    isMobileEditSizesModalOpen,
    setIsMobileEditSizesModalOpen,
    isEditSizesModalOpen,
    setIsEditSizesModalOpen,
    isAddBikeModalOpen,
    setIsAddBikeModalOpen,
    topFilter,
    shouldShowBothFilters,
    filterVariant,
    onAddBikeClick,
    onSyncSelectedBikes,
    onSelectSize,
  } = useMySizesGarage({
    userDoc,
    facet_counts,
    handleBikeSelection,
    filterState,
    handleSizeSelection,
    bikePendingFilters,
    sizePendingFilters,
  });

  const userSizesFlatList: {
    type: keyof UserSizes;
    value: string | number | undefined;
    enabled: boolean;
  }[] = userDoc?.sizes
    ? [
        {
          type: 'helmet',
          value: userDoc.sizes.helmet,
          enabled: !!selectedSizes?.helmet,
        },
        {
          type: 'jersey',
          value: userDoc.sizes.jersey,
          enabled: !!selectedSizes?.jersey,
        },
        {
          type: 'pants',
          value: userDoc.sizes.pants,
          enabled: !!selectedSizes?.pants,
        },
        {
          type: 'boots',
          value: userDoc.sizes.boots,
          enabled: !!selectedSizes?.boots,
        },
      ]
    : [];

  if (!userDoc || userDoc.name === 'Anonymous User') {
    return (
      <div className="flex h-fit w-full flex-col items-start gap-6 border border-[#D9D9D9] p-6">
        <div className="text-[1.8rem] font-semibold text-black">
          My sizes & garage
        </div>

        <p className="text-[1.6rem] font-medium text-[#757575]">
          Sign up or log in to add your sizes and vehicles. We&apos;ll
          personalize your feed to show only listings that fit you and your
          ride.
        </p>

        <Link
          href={'/login'}
          className="bg-transparent text-[1.6rem] font-semibold capitalize text-brand-secondary transition-colors duration-150 hover:text-brand-secondary-darker"
        >
          Login Now
        </Link>
      </div>
    );
  }

  if (shouldShowBothFilters) {
    const filters =
      topFilter === 'gear' ? ['gear', 'parts'] : ['parts', 'gear'];

    return (
      <div className="flex w-full flex-col gap-6">
        {filters.map((filter) =>
          filter === 'gear' ? (
            <GearFilter
              key="gear-filter"
              isEditSizesModalOpen={isEditSizesModalOpen}
              setIsEditSizesModalOpen={setIsEditSizesModalOpen}
              isMobileEditSizesModalOpen={isMobileEditSizesModalOpen}
              setIsMobileEditSizesModalOpen={setIsMobileEditSizesModalOpen}
              userDoc={userDoc}
              userSizesFlatList={userSizesFlatList}
              selectedSizes={selectedSizes}
              onSelectSize={onSelectSize}
              handleSizeSelection={handleSizeSelection}
            />
          ) : (
            <GarageFilter
              key="garage-filter"
              isEditGarageModalOpen={isEditGarageModalOpen}
              setIsEditGarageModalOpen={setIsEditGarageModalOpen}
              isAddBikeModalOpen={isAddBikeModalOpen}
              setIsAddBikeModalOpen={setIsAddBikeModalOpen}
              userDoc={userDoc}
              selectedBikes={selectedBikes}
              setSelectedBikes={setSelectedBikes}
              handleBikeSelection={handleBikeSelection}
              onAddBikeClick={onAddBikeClick}
              onSyncSelectedBikes={onSyncSelectedBikes}
              isMobileEditGarageModalOpen={isMobileEditGarageModalOpen}
              setIsMobileEditGarageModalOpen={setIsMobileEditGarageModalOpen}
            />
          )
        )}
      </div>
    );
  }

  return filterVariant === 'gear' ? (
    <>
      {!!userSizesFlatList.length && (
        <GearFilter
          isEditSizesModalOpen={isEditSizesModalOpen}
          setIsEditSizesModalOpen={setIsEditSizesModalOpen}
          isMobileEditSizesModalOpen={isMobileEditSizesModalOpen}
          setIsMobileEditSizesModalOpen={setIsMobileEditSizesModalOpen}
          userDoc={userDoc}
          userSizesFlatList={userSizesFlatList}
          selectedSizes={selectedSizes}
          onSelectSize={onSelectSize}
          handleSizeSelection={handleSizeSelection}
        />
      )}
    </>
  ) : (
    <GarageFilter
      isEditGarageModalOpen={isEditGarageModalOpen}
      setIsEditGarageModalOpen={setIsEditGarageModalOpen}
      isAddBikeModalOpen={isAddBikeModalOpen}
      setIsAddBikeModalOpen={setIsAddBikeModalOpen}
      userDoc={userDoc}
      selectedBikes={selectedBikes}
      setSelectedBikes={setSelectedBikes}
      handleBikeSelection={handleBikeSelection}
      onAddBikeClick={onAddBikeClick}
      onSyncSelectedBikes={onSyncSelectedBikes}
      isMobileEditGarageModalOpen={isMobileEditGarageModalOpen}
      setIsMobileEditGarageModalOpen={setIsMobileEditGarageModalOpen}
    />
  );
}
